import { useCallback, useRef } from 'react'

import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import logger from '@cash-web/shared/util-logger'
import { useLogCashWebCdfEvent, useLogCashWebCdfEventImmediately } from '@cash-web/shared-feature-analytics'
import {
  BlockerFlowInteractViewBlocker,
  FlowsInteractSubmit,
  WebTaxesLoginSuccess,
} from '@squareup/customer-data-events-web'

// Log a blocker start event every time a new
// new blockerName or clientScenario is given.
// eslint-disable-next-line import/prefer-default-export
export function useLogBlockerStartEvent() {
  const logCDFEvent = useLogCashWebCdfEvent()
  const logCDFEventRef = useRef(logCDFEvent)

  return useCallback(
    ({
      clientScenario,
      blockerName,
      blockerDescriptorId,
      flowToken,
    }: {
      clientScenario?: string
      blockerName?: string
      blockerDescriptorId?: string
      flowToken?: string
    }) => {
      const viewBlockerEvent = BlockerFlowInteractViewBlocker({
        blockerId: blockerDescriptorId,
        blockerType: blockerName,
        clientScenario,
        flowToken,
      })
      logCDFEventRef.current(viewBlockerEvent)

      logger.addBreadcrumb('Blocker start', { blockerName, clientScenario, blockerDescriptorId })
    },
    []
  )
}

interface BlockerEventData {
  clientScenario?: string
  blockerName: string
  blockerDescriptorId?: string
  flowToken: string
  flowName?: string
  terminalBlocker?: boolean
}

export function useLogBlockerSuccessEvent() {
  const logCdfEvent = useLogCashWebCdfEvent()

  return ({
    clientScenario,
    blockerName,
    blockerDescriptorId,
    flowToken,
    flowName,
    terminalBlocker,
  }: BlockerEventData) => {
    logCdfEvent(
      FlowsInteractSubmit({
        flowToken,
        rootFlowToken: flowToken,
        flowName,
        blockerSubmitResult: 'SUCCESS',
        blockerId: blockerDescriptorId,
        terminalBlocker,
      })
    )

    logger.addBreadcrumb('Blocker submit success', { blockerName, clientScenario })
  }
}

export function useLogBlockerErrorEvent() {
  const logCdfEvent = useLogCashWebCdfEvent()

  return ({
    clientScenario,
    blockerName,
    blockerDescriptorId,
    flowToken,
    flowName,
    terminalBlocker,
  }: BlockerEventData) => {
    logCdfEvent(
      FlowsInteractSubmit({
        flowToken,
        rootFlowToken: flowToken,
        flowName,
        blockerSubmitResult: 'FAILED',
        blockerId: blockerDescriptorId,
        terminalBlocker,
      })
    )

    logger.addBreadcrumb('Blocker submit error', { blockerName, clientScenario })
  }
}

export function useTaxLoginSuccessEvent() {
  const logCdfEvent = useLogCashWebCdfEventImmediately()
  const clientScenario = ClientScenario.INITIATE_TAX_UPGRADE
  return async (type: 'tax_web_view_blocker' | 'instant_redirect' = 'tax_web_view_blocker') => {
    await logCdfEvent(
      WebTaxesLoginSuccess({
        category: clientScenario,
        type: type,
      })
    )
    logger.addBreadcrumb('Tax login success', { blockerName: type, clientScenario })
  }
}
